import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import * as React from 'react';
import type { ReactNode } from 'react';
import { assertNever } from '../Utils/typeUtils';
import { Collapse } from '~/Partials/Collapse/Collapse';
import type { Theme } from '~/Utils/Theme';
import { themeDefaultValues } from '~/Utils/Theme';
import styles from './AccordionItem.module.css';
import { AccordionExpander } from './Components/AccordionExpander';
import type { AccordionExpanderProps, BaseAccordionExpanderProps } from './Components/AccordionExpander';
import { useAccordionContentRenderer } from './Hooks/useAccordionContentRenderer';
import type { AccordionIcon } from './Types/AccordionIcon';
import type { AccordionSpacing } from './Types/AccordionSpacing';

interface BaseAccordionItemProps extends Pick<AccordionExpanderProps, 'color' | 'variant'> {
    children: ReactNode;
    index?: string;
    initialExpanded?: boolean;
    isExpandable?: boolean;
    theme?: Theme;
    contentSpacing?: AccordionSpacing;
    externallyExpanded?: boolean;
    lazyRender?: boolean;
    className?: string;
    contentClassName?: string;
}

export interface DefaultAccordionItemProps extends BaseAccordionItemProps {
    headerContent: ReactNode;
    expanderClassName?: string;
    iconType?: AccordionIcon;
    type?: 'default';
}

export interface CustomAccordionItemProps extends BaseAccordionItemProps {
    renderCustomExpander: (expanderProps: BaseAccordionExpanderProps) => ReactNode;
    type: 'custom';
}

export type AccordionItemProps = DefaultAccordionItemProps | CustomAccordionItemProps;

export const AccordionItem: React.FC<React.PropsWithChildren<AccordionItemProps>> = props => {
    const {
        children,
        index,
        className,
        contentClassName,
        externallyExpanded,
        type,
        contentSpacing,
        lazyRender = false,
        initialExpanded = false,
        isExpandable = true,
        variant = 'solid',
        color = 'grey',
        theme = themeDefaultValues,
    } = props;
    const { withRoundedCorners, withBoxShadow } = theme;

    const [expanded, setExpanded] = React.useState(initialExpanded);
    const accordionId = index || uniqueId('AccordionItem__');

    React.useEffect(() => {
        externallyExpanded && setExpanded(true);
    }, [externallyExpanded]);

    const { content, onToggleClick, onCollapsed } = useAccordionContentRenderer(children, lazyRender, expanded);

    const onHeaderClicked = () => {
        onToggleClick();
        setExpanded(!expanded);
    };

    const renderExpander = () => {
        const baseExpanderProps: BaseAccordionExpanderProps = {
            onClick: onHeaderClicked,
            'aria-expanded': expanded,
            'aria-controls': accordionId,
            expanded,
            disabled: !isExpandable,
            color,
            variant,
        };

        switch (type) {
            case 'default':
            case undefined:
                const { iconType, headerContent, expanderClassName } = props;

                return (
                    <AccordionExpander {...baseExpanderProps} className={expanderClassName} iconType={iconType}>
                        {headerContent}
                    </AccordionExpander>
                );
            case 'custom':
                const { renderCustomExpander } = props;

                return renderCustomExpander(baseExpanderProps);
            default:
                assertNever(props);
        }
    };

    return (
        <div
            className={classNames(
                styles[`Variant--${variant}`],
                styles[`Variant--${color}`],
                contentSpacing && styles[`Variant--${contentSpacing}`],
                withBoxShadow && styles['Variant--withBoxShadow'],
                withRoundedCorners && styles['Variant--rounded'],
                !expanded && styles['Variant--hidden'],
                className
            )}
        >
            {renderExpander()}
            <Collapse isOpen={expanded} onRest={onCollapsed} className={classNames(styles.Content, contentClassName)}>
                <div id={accordionId} className={styles.ContentInner}>
                    {content}
                </div>
            </Collapse>
        </div>
    );
};
